import * as THREE from 'three/webgpu';
import type {FeatureResultMesh} from '../mesh/feature';
import {isFeatureResultMesh} from '../mesh/feature';
import {resolveImportedModelTopologyRepository} from '../imported-model/internal';
import {CameraModelSpatialQuery} from './CameraModelSpatialQuery';
import {CameraTopologySnapshotSource} from './CameraTopologySnapshotSource';
import type {ViewportRotationFocusResolverInput, ViewportRotationFocusResult, ScreenBoundsMetrics, ViewportRotationTopologyProxyKind as TopologyProxyKind} from '../../types';
export type {ViewportRotationFocusResolverInput, ViewportRotationFocusResult} from '../../types';


/**
 * 视口旋转关注目标类型。
 */
export const ViewportRotationFocusTargetKind = {
    /** 未解析到明确的中心关注目标。 */
    None: 'none',
    /** 旋转中心来自中心关注的特征结果体。 */
    FeatureResultMesh: 'feature-result-mesh',
    /** 旋转中心来自无须创建特征宿主的业务模型或实例。 */
    Model: 'model',
    /** 旋转中心来自中心关注结果体下的拓扑代理。 */
    TopologyProxy: 'topology-proxy',
} as const;

/**
 * 视口旋转拓扑代理类型。
 */
export const ViewportRotationTopologyProxyKind = {
    /** 拓扑点代理。 */
    FeaturePoint: 'feature-point',
    /** 拓扑线代理。 */
    FeatureLine: 'feature-line',
    /** 拓扑面代理。 */
    FeatureFace: 'feature-face',
} as const;

/**
 * 当前视口旋转关注目标解析器。
 *
 * 设计说明：
 * 1. `CameraManager` 只负责注入场景、相机和过滤规则，本类专注“谁是当前屏幕中心关注对象”；
 * 2. 按单个实例/结果体评分，不让同批离屏实例拉偏当前观察对象；
 * 3. 确定业务来源后只读取其拓扑快照，不依赖是否已生成 hover 宿主；
 * 4. 该类复用向量、包围盒和数组缓存，避免旋转导航热路径产生多余临时对象。
 */
export class ViewportRotationFocusResolver {
    /** 归一化视口中心 X。 */
    private static readonly VIEWPORT_CENTER_X = 0.5;

    /** 归一化视口中心 Y。 */
    private static readonly VIEWPORT_CENTER_Y = 0.5;

    /** 能被认为是中心关注对象的最大屏幕中心距离。 */
    private static readonly FOCUS_CENTER_DISTANCE_LIMIT = 0.18;

    /** 能被认为是中心关注对象的最小视口内面积占比。 */
    private static readonly FOCUS_MIN_SCREEN_AREA = 0.004;

    /** 能被认为是中心关注对象的最小可见比例。 */
    private static readonly FOCUS_MIN_VISIBLE_RATIO = 0.3;

    /** 数值安全阈值。 */
    private static readonly EPSILON = 1e-8;

    /** 拓扑代理距离评分中的类型平局偏移。 */
    private static readonly TOPOLOGY_PROXY_PRIORITY_WEIGHT = 1e-4;

    /** 能被认为靠近屏幕中心的拓扑代理最大屏幕距离。 */
    private static readonly TOPOLOGY_PROXY_CENTER_DISTANCE_LIMIT = 0.24;

    /** 候选结果体包围盒缓存。 */
    private readonly candidateBounds: THREE.Box3 = new THREE.Box3();

    /** 最佳结果体包围盒缓存。 */
    private readonly bestFeatureBounds: THREE.Box3 = new THREE.Box3();

    /** 拓扑代理包围盒缓存。 */
    private readonly topologyProxyBounds: THREE.Box3 = new THREE.Box3();

    /** 最佳拓扑代理包围盒缓存。 */
    private readonly bestTopologyProxyBounds: THREE.Box3 = new THREE.Box3();

    /** 结果包围盒缓存。 */
    private readonly resultBounds: THREE.Box3 = new THREE.Box3();

    /** 结果中心缓存。 */
    private readonly resultCenter: THREE.Vector3 = new THREE.Vector3();

    /** 当前拓扑代理中心缓存。 */
    private readonly topologyProxyCenter: THREE.Vector3 = new THREE.Vector3();

    /** 投影点缓存。 */
    private readonly projectedPoint: THREE.Vector3 = new THREE.Vector3();

    /** 屏幕包围盒八角点缓存。 */
    private readonly boxCorners: THREE.Vector3[] = Array.from({ length: 8 }, () => new THREE.Vector3());

    /** 屏幕空间评分缓存。 */
    private readonly screenBoundsMetrics: ScreenBoundsMetrics = {
        rawMinX: 0,
        rawMaxX: 0,
        rawMinY: 0,
        rawMaxY: 0,
        clippedCenterX: 0,
        clippedCenterY: 0,
        clippedArea: 0,
        visibleRatio: 0,
        centerDistance: Number.POSITIVE_INFINITY,
        containsViewportCenter: false,
    };

    /** 普通/实例统一候选，矩阵来自当前可见槽而非 source 的物理父链。 */
    private bestSourceMesh: THREE.Mesh | null = null;
    /** 最佳来源的世界矩阵副本。 */
    private readonly bestSourceWorldMatrix = new THREE.Matrix4();
    /** 默认普通场景查询；CameraManager 注入带 Unit 注册的正式入口。 */
    private readonly fallbackSpatialQuery = new CameraModelSpatialQuery();
    /** 本轮胜出的拓扑资产，不创建宿主。 */
    private bestTopologySource: CameraTopologySnapshotSource | null = null;
    /** 当前最佳结果体。 */
    private bestFeatureResultMesh: FeatureResultMesh | null = null;

    /** 当前最佳结果体屏幕中心距离。 */
    private bestFeatureCenterDistance: number = Number.POSITIVE_INFINITY;

    /** 当前最佳结果体视口内面积。 */
    private bestFeatureClippedArea: number = 0;

    /** 当前最佳结果体可见比例。 */
    private bestFeatureVisibleRatio: number = 0;

    /** 当前最佳结果体是否覆盖视口中心。 */
    private bestFeatureContainsViewportCenter: boolean = false;

    /** 当前最佳拓扑代理评分。 */
    private bestTopologyProxyScore: number = Number.POSITIVE_INFINITY;

    /** 当前最佳拓扑代理类型。 */
    private bestTopologyProxyKind: TopologyProxyKind | null = null;

    /** 当前最佳拓扑代理索引。 */
    private bestTopologyProxyIndex: number | null = null;

    /** 解析结果缓存。 */
    private readonly result: ViewportRotationFocusResult = {
        kind: ViewportRotationFocusTargetKind.None,
        center: this.resultCenter,
        bounds: this.resultBounds,
        featureResultMesh: null,
        sourceMesh: null,
        topologySource: null,
        topologyProxyKind: null,
        topologyProxyIndex: null,
    };

    /**
     * 解析当前视口中心关注目标。
     *
     * @param input 解析输入。
     * @returns 中心关注解析结果；没有明确中心关注对象时返回 `null`。
     */
    public resolve(input: ViewportRotationFocusResolverInput): ViewportRotationFocusResult | null {
        this.resetFeatureCandidateState();
        (input.spatialQuery ?? this.fallbackSpatialQuery).forEachModel(input.scene, input.shouldParticipate,
            (source, bounds, matrix, instanced) => this.tryAcceptModelCandidate(source, bounds, matrix, instanced, input));

        if (!this.bestSourceMesh) return null;
        this.result.sourceMesh = this.bestSourceMesh;
        if (this.resolveTopologyProxyFocus(this.bestSourceMesh, input.camera)) {
            this.result.kind = ViewportRotationFocusTargetKind.TopologyProxy;
            this.result.featureResultMesh = this.bestFeatureResultMesh;
            this.result.topologySource = this.bestTopologySource;
            this.result.topologyProxyKind = this.bestTopologyProxyKind;
            this.result.topologyProxyIndex = this.bestTopologyProxyIndex;
            this.result.bounds.copy(this.bestTopologyProxyBounds);
            this.result.bounds.getCenter(this.result.center);
            return this.result;
        }

        this.result.kind = this.bestFeatureResultMesh ? ViewportRotationFocusTargetKind.FeatureResultMesh : ViewportRotationFocusTargetKind.Model;
        this.result.featureResultMesh = this.bestFeatureResultMesh;
        this.result.topologyProxyKind = null;
        this.result.topologyProxyIndex = null;
        this.result.bounds.copy(this.bestFeatureBounds);
        this.result.bounds.getCenter(this.result.center);
        return this.result;
    }

    /** 销毁或结束会话时释放最后一次来源引用，不拥有或销毁模型资产。 */
    public dispose(): void {
        this.resetFeatureCandidateState();
        this.fallbackSpatialQuery.dispose();
    }

    /**
     * 重置结果体候选状态。
     */
    private resetFeatureCandidateState(): void {
        this.bestFeatureResultMesh = null;
        this.bestSourceMesh = null;
        this.bestTopologySource = null;
        this.result.sourceMesh = null;
        this.result.topologySource = null;
        this.bestFeatureBounds.makeEmpty();
        this.bestFeatureCenterDistance = Number.POSITIVE_INFINITY;
        this.bestFeatureClippedArea = 0;
        this.bestFeatureVisibleRatio = 0;
        this.bestFeatureContainsViewportCenter = false;
        this.result.kind = ViewportRotationFocusTargetKind.None;
        this.result.featureResultMesh = null;
        this.result.topologyProxyKind = null;
        this.result.topologyProxyIndex = null;
    }

    /**
     * 尝试接收一个特征结果体候选。
     *
     * @param object 场景对象。
     * @param bounds 单源世界范围。
     * @param matrix 单源权威世界矩阵。
     * @param instanced 是否来自单个实例槽。
     * @param input 解析输入。
     */
    private tryAcceptModelCandidate(
        object: THREE.Object3D,
        bounds: THREE.Box3,
        matrix: THREE.Matrix4,
        instanced: boolean,
        input: ViewportRotationFocusResolverInput,
    ): void {
        // 普通无拓扑 Mesh 保持合并包围盒兜底；实例与导入 Body 可进入局部关注路径。
        if (!(object instanceof THREE.Mesh) || (!isFeatureResultMesh(object) && !instanced && !resolveImportedModelTopologyRepository(object))) return;
        this.candidateBounds.copy(bounds);
        if (this.candidateBounds.isEmpty() || !input.frustum.intersectsBox(this.candidateBounds)) {
            return;
        }

        if (!this.updateScreenBoundsMetrics(this.candidateBounds, input.camera)) {
            return;
        }

        if (!this.isFocusedScreenBounds(this.screenBoundsMetrics)) {
            return;
        }

        if (!this.shouldReplaceBestFeatureCandidate(this.screenBoundsMetrics)) {
            return;
        }

        this.bestFeatureResultMesh = isFeatureResultMesh(object) ? object : null;
        this.bestSourceMesh = object;
        this.bestSourceWorldMatrix.copy(matrix);
        this.bestFeatureBounds.copy(this.candidateBounds);
        this.bestFeatureCenterDistance = this.screenBoundsMetrics.centerDistance;
        this.bestFeatureClippedArea = this.screenBoundsMetrics.clippedArea;
        this.bestFeatureVisibleRatio = this.screenBoundsMetrics.visibleRatio;
        this.bestFeatureContainsViewportCenter = this.screenBoundsMetrics.containsViewportCenter;
    }

    /**
     * 判断当前屏幕投影是否能视为中心关注对象。
     *
     * @param metrics 屏幕投影评分。
     * @returns 是否为中心关注对象。
     */
    private isFocusedScreenBounds(metrics: ScreenBoundsMetrics): boolean {
        if (metrics.clippedArea < ViewportRotationFocusResolver.FOCUS_MIN_SCREEN_AREA) {
            return false;
        }

        if (metrics.visibleRatio < ViewportRotationFocusResolver.FOCUS_MIN_VISIBLE_RATIO) {
            return false;
        }

        return metrics.containsViewportCenter
            || metrics.centerDistance <= ViewportRotationFocusResolver.FOCUS_CENTER_DISTANCE_LIMIT;
    }

    /**
     * 判断当前结果体候选是否应替换最佳候选。
     *
     * @param metrics 当前候选评分。
     * @returns 是否替换。
     */
    private shouldReplaceBestFeatureCandidate(metrics: ScreenBoundsMetrics): boolean {
        if (!this.bestSourceMesh) {
            return true;
        }

        if (metrics.containsViewportCenter !== this.bestFeatureContainsViewportCenter) {
            return metrics.containsViewportCenter;
        }

        const distanceDelta = metrics.centerDistance - this.bestFeatureCenterDistance;
        if (Math.abs(distanceDelta) > ViewportRotationFocusResolver.EPSILON) {
            return distanceDelta < 0;
        }

        const visibleRatioDelta = metrics.visibleRatio - this.bestFeatureVisibleRatio;
        if (Math.abs(visibleRatioDelta) > ViewportRotationFocusResolver.EPSILON) {
            return visibleRatioDelta > 0;
        }

        return metrics.clippedArea > this.bestFeatureClippedArea;
    }

    /**
     * 解析中心关注结果体内部的拓扑代理。
     *
     * @param mesh 中心关注结果体。
     * @param camera 当前相机。
     * @returns 是否命中拓扑代理。
     */
    private resolveTopologyProxyFocus(
        mesh: THREE.Mesh,
        camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    ): boolean {
        this.bestTopologyProxyScore = Number.POSITIVE_INFINITY;
        this.bestTopologyProxyKind = null;
        this.bestTopologyProxyIndex = null;
        const sources: CameraTopologySnapshotSource[] = [];
        if (isFeatureResultMesh(mesh)) {
            const snapshot = mesh.getTopologySnapshot();
            if (snapshot) sources.push(new CameraTopologySnapshotSource(mesh, this.bestSourceWorldMatrix, snapshot));
        } else {
            const repository = resolveImportedModelTopologyRepository(mesh);
            for (const binding of repository?.resolveBodyRecordsByVisibleMesh(mesh) ?? []) {
                const data = binding.topologyAsset.meshData;
                if (data.topologySnapshot) sources.push(new CameraTopologySnapshotSource(mesh, this.bestSourceWorldMatrix, data.topologySnapshot, data));
            }
        }
        // 仅在最佳业务来源内部比较拓扑，沿用原来的屏幕中心距离及点/线/面平局优先级。
        for (const source of sources) {
            for (const face of source.snapshot.faces) this.tryAcceptSnapshot(source, ViewportRotationTopologyProxyKind.FeatureFace, face.index, camera);
            for (const edge of source.snapshot.edges) this.tryAcceptSnapshot(source, ViewportRotationTopologyProxyKind.FeatureLine, edge.index, camera);
            for (const vertex of source.snapshot.vertices) this.tryAcceptSnapshot(source, ViewportRotationTopologyProxyKind.FeaturePoint, vertex.index, camera);
        }
        return this.bestTopologyProxyKind !== null;
    }

    /** @param source 权威拓扑空间。 @param kind 元素种类。 @param index 稳定索引。 @param camera 当前相机。 */
    private tryAcceptSnapshot(source: CameraTopologySnapshotSource, kind: TopologyProxyKind, index: number,
        camera: THREE.PerspectiveCamera | THREE.OrthographicCamera): void {
        if (!source.getWorldBounds(kind, index, this.topologyProxyBounds)) return;
        const previousScore = this.bestTopologyProxyScore;
        this.tryAcceptTopologyBounds(this.topologyProxyBounds, kind, index, camera);
        if (this.bestTopologyProxyScore < previousScore) this.bestTopologySource = source;
    }

    /**
     * 尝试接收一个拓扑包围盒候选。
     *
     * @param bounds 拓扑代理世界包围盒。
     * @param kind 拓扑代理类型。
     * @param topologyProxyIndex 拓扑代理索引。
     * @param camera 当前相机。
     */
    private tryAcceptTopologyBounds(
        bounds: THREE.Box3,
        kind: TopologyProxyKind,
        topologyProxyIndex: number,
        camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    ): void {
        bounds.getCenter(this.topologyProxyCenter);
        if (!this.projectWorldPointToViewport(this.topologyProxyCenter, camera)) {
            return;
        }

        const distance = Math.hypot(
            this.projectedPoint.x - ViewportRotationFocusResolver.VIEWPORT_CENTER_X,
            this.projectedPoint.y - ViewportRotationFocusResolver.VIEWPORT_CENTER_Y,
        );
        if (distance > ViewportRotationFocusResolver.TOPOLOGY_PROXY_CENTER_DISTANCE_LIMIT) {
            return;
        }

        const score = distance
            + this.resolveTopologyProxyPriority(kind) * ViewportRotationFocusResolver.TOPOLOGY_PROXY_PRIORITY_WEIGHT;

        if (score >= this.bestTopologyProxyScore) {
            return;
        }

        this.bestTopologyProxyScore = score;
        this.bestTopologyProxyKind = kind;
        this.bestTopologyProxyIndex = topologyProxyIndex;
        this.bestTopologyProxyBounds.copy(bounds);
    }

    /**
     * 读取拓扑代理平局优先级。
     *
     * @param kind 拓扑代理类型。
     * @returns 平局优先级；数值越小优先级越高。
     */
    private resolveTopologyProxyPriority(kind: TopologyProxyKind): number {
        switch (kind) {
            case ViewportRotationTopologyProxyKind.FeaturePoint:
                return 0;
            case ViewportRotationTopologyProxyKind.FeatureLine:
                return 1;
            case ViewportRotationTopologyProxyKind.FeatureFace:
            default:
                return 2;
        }
    }

    /**
     * 更新包围盒屏幕空间评分。
     *
     * @param bounds 世界包围盒。
     * @param camera 当前相机。
     * @returns 是否得到有效屏幕投影。
     */
    private updateScreenBoundsMetrics(
        bounds: THREE.Box3,
        camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    ): boolean {
        this.writeBoxCorners(bounds);

        let rawMinX = Number.POSITIVE_INFINITY;
        let rawMaxX = Number.NEGATIVE_INFINITY;
        let rawMinY = Number.POSITIVE_INFINITY;
        let rawMaxY = Number.NEGATIVE_INFINITY;
        let projectedCount = 0;

        this.boxCorners.forEach((corner) => {
            if (!this.projectWorldPointToViewport(corner, camera)) {
                return;
            }
            rawMinX = Math.min(rawMinX, this.projectedPoint.x);
            rawMaxX = Math.max(rawMaxX, this.projectedPoint.x);
            rawMinY = Math.min(rawMinY, this.projectedPoint.y);
            rawMaxY = Math.max(rawMaxY, this.projectedPoint.y);
            projectedCount += 1;
        });

        if (projectedCount <= 0) {
            return false;
        }

        const clippedMinX = ViewportRotationFocusResolver.clamp01(rawMinX);
        const clippedMaxX = ViewportRotationFocusResolver.clamp01(rawMaxX);
        const clippedMinY = ViewportRotationFocusResolver.clamp01(rawMinY);
        const clippedMaxY = ViewportRotationFocusResolver.clamp01(rawMaxY);
        const clippedWidth = clippedMaxX - clippedMinX;
        const clippedHeight = clippedMaxY - clippedMinY;
        if (clippedWidth <= ViewportRotationFocusResolver.EPSILON
            || clippedHeight <= ViewportRotationFocusResolver.EPSILON) {
            return false;
        }

        const rawArea = Math.max(
            (rawMaxX - rawMinX) * (rawMaxY - rawMinY),
            ViewportRotationFocusResolver.EPSILON,
        );
        const clippedArea = clippedWidth * clippedHeight;
        const clippedCenterX = (clippedMinX + clippedMaxX) * 0.5;
        const clippedCenterY = (clippedMinY + clippedMaxY) * 0.5;

        this.screenBoundsMetrics.rawMinX = rawMinX;
        this.screenBoundsMetrics.rawMaxX = rawMaxX;
        this.screenBoundsMetrics.rawMinY = rawMinY;
        this.screenBoundsMetrics.rawMaxY = rawMaxY;
        this.screenBoundsMetrics.clippedCenterX = clippedCenterX;
        this.screenBoundsMetrics.clippedCenterY = clippedCenterY;
        this.screenBoundsMetrics.clippedArea = clippedArea;
        this.screenBoundsMetrics.visibleRatio = Math.min(clippedArea / rawArea, 1);
        this.screenBoundsMetrics.centerDistance = Math.hypot(
            clippedCenterX - ViewportRotationFocusResolver.VIEWPORT_CENTER_X,
            clippedCenterY - ViewportRotationFocusResolver.VIEWPORT_CENTER_Y,
        );
        this.screenBoundsMetrics.containsViewportCenter = rawMinX <= ViewportRotationFocusResolver.VIEWPORT_CENTER_X
            && rawMaxX >= ViewportRotationFocusResolver.VIEWPORT_CENTER_X
            && rawMinY <= ViewportRotationFocusResolver.VIEWPORT_CENTER_Y
            && rawMaxY >= ViewportRotationFocusResolver.VIEWPORT_CENTER_Y;
        return true;
    }

    /**
     * 将世界点投影到归一化视口坐标。
     *
     * @param point 世界点。
     * @param camera 当前相机。
     * @returns 是否投影成功。
     */
    private projectWorldPointToViewport(
        point: THREE.Vector3,
        camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    ): boolean {
        this.projectedPoint.copy(point).project(camera);
        if (!Number.isFinite(this.projectedPoint.x)
            || !Number.isFinite(this.projectedPoint.y)
            || !Number.isFinite(this.projectedPoint.z)) {
            return false;
        }

        this.projectedPoint.set(
            (this.projectedPoint.x + 1) * 0.5,
            (1 - this.projectedPoint.y) * 0.5,
            this.projectedPoint.z,
        );
        return true;
    }

    /**
     * 写入包围盒八个角点。
     *
     * @param bounds 世界包围盒。
     */
    private writeBoxCorners(bounds: THREE.Box3): void {
        const { min, max } = bounds;
        this.boxCorners[0].set(min.x, min.y, min.z);
        this.boxCorners[1].set(min.x, min.y, max.z);
        this.boxCorners[2].set(min.x, max.y, min.z);
        this.boxCorners[3].set(min.x, max.y, max.z);
        this.boxCorners[4].set(max.x, min.y, min.z);
        this.boxCorners[5].set(max.x, min.y, max.z);
        this.boxCorners[6].set(max.x, max.y, min.z);
        this.boxCorners[7].set(max.x, max.y, max.z);
    }

    /**
     * 归一化裁剪。
     *
     * @param value 待裁剪数值。
     * @returns 裁剪后的数值。
     */
    private static clamp01(value: number): number {
        return Math.min(Math.max(value, 0), 1);
    }

}

